/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * UI-only product form fields shared by create & edit modal modes.
 * Does not alter API payloads, field keys, or validation rules.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiTrash2,
  FiX,
  FiUploadCloud,
  FiImage,
  FiTag,
  FiPackage,
  FiDollarSign,
  FiLayers,
  FiChevronDown,
  FiSearch,
  FiAlertCircle,
  FiCheck,
} from "react-icons/fi";
import { useUI } from "../theme/ThemeProvider";

const SECTION_IDS = ["basics", "pricing", "variants"];

function ModernField({
  label,
  value,
  onChange,
  type = "text",
  error,
  placeholder,
  hint,
  required,
  suffix,
}) {
  const { colors, isDark } = useUI();

  const handleInputChange = (e) => {
    let val = e.target.value;
    if (type === "number") {
      val = val.replace(/[^0-9.]/g, "");
      const parts = val.split(".");
      if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
    }
    onChange(val);
  };

  return (
    <label className="block group">
      <span
        className="text-[11px] font-semibold mb-1 flex items-center gap-0.5"
        style={{ color: error ? "#EF4444" : colors.subText }}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <div className="relative">
        <input
          type={type === "number" ? "text" : type}
          inputMode={type === "number" ? "decimal" : undefined}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={handleInputChange}
          onWheel={(e) => e.target.blur()}
          className="w-full rounded-lg px-2.5 py-1.5 outline-none transition-all duration-150 text-[13px]"
          style={{
            backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#f8fafc",
            border: `1px solid ${error ? "#EF4444" : colors.border}`,
            color: colors.text,
            paddingInlineEnd: suffix ? "2.25rem" : undefined,
          }}
          onFocus={(e) => {
            e.target.style.borderColor = error ? "#EF4444" : colors.inputFocus;
            e.target.style.boxShadow = `0 0 0 2px ${error ? "#EF444420" : colors.inputFocus + "22"}`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? "#EF4444" : colors.border;
            e.target.style.boxShadow = "none";
          }}
        />
        {suffix && (
          <span
            className="absolute top-1/2 -translate-y-1/2 text-[10px] font-semibold opacity-55 pointer-events-none"
            style={{ insetInlineEnd: "0.65rem", color: colors.subText }}
          >
            {suffix}
          </span>
        )}
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -2 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-[10px] font-medium text-red-500 flex items-center gap-1"
        >
          <FiAlertCircle size={11} />
          {typeof error === "string" ? error : hint || ""}
        </motion.p>
      )}
      {!error && hint && (
        <p className="mt-0.5 text-[9px] opacity-50" style={{ color: colors.subText }}>
          {hint}
        </p>
      )}
    </label>
  );
}

function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  required,
  action,
}) {
  const { colors, isDark, isArabic } = useUI();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const selected = options.find((o) => Number(o.id) === Number(value));
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.name || "").toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={rootRef} className={`relative ${error ? "animate-pulse" : ""}`}>
      <div className="flex items-center justify-between mb-1">
        <span
          className="text-[11px] font-semibold flex items-center gap-0.5"
          style={{ color: error ? "#EF4444" : colors.subText }}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </span>
        {action}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="w-full rounded-lg px-2.5 py-1.5 text-[13px] text-start flex items-center justify-between gap-2 transition-all duration-150 disabled:opacity-50"
        style={{
          backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#f8fafc",
          border: `1px solid ${error ? "#EF4444" : colors.border}`,
          color: selected ? colors.text : colors.subText,
        }}
      >
        <span className="truncate">{selected?.name || placeholder}</span>
        <FiChevronDown
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          style={{ color: colors.subText }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1.5 w-full rounded-xl border shadow-xl overflow-hidden"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <div className="p-2 border-b" style={{ borderColor: colors.border }}>
              <div className="relative">
                <FiSearch
                  size={14}
                  className="absolute top-1/2 -translate-y-1/2 opacity-40"
                  style={{ insetInlineStart: "0.75rem" }}
                />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={isArabic ? "بحث..." : "Search..."}
                  className="w-full rounded-lg py-2 text-sm outline-none"
                  style={{
                    paddingInlineStart: "2rem",
                    paddingInlineEnd: "0.75rem",
                    backgroundColor: isDark ? "rgba(15,23,42,0.8)" : "#f1f5f9",
                    color: colors.text,
                  }}
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              <button
                type="button"
                onClick={() => {
                  onChange(0);
                  setOpen(false);
                  setSearch("");
                }}
                className="w-full px-3 py-2 text-sm text-start transition-colors"
                style={{ color: colors.subText }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                {placeholder}
              </button>
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-xs opacity-50 text-center">
                  {isArabic ? "لا توجد نتائج" : "No results"}
                </p>
              ) : (
                filtered.map((opt) => {
                  const active = Number(opt.id) === Number(value);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        onChange(opt.id);
                        setOpen(false);
                        setSearch("");
                      }}
                      className="w-full px-3 py-2 text-sm text-start flex items-center justify-between gap-2 transition-colors"
                      style={{
                        color: colors.text,
                        backgroundColor: active
                          ? `${colors.mainColor}18`
                          : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!active)
                          e.currentTarget.style.backgroundColor = isDark
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = active
                          ? `${colors.mainColor}18`
                          : "transparent";
                      }}
                    >
                      <span className="truncate">{opt.name}</span>
                      {active && <FiCheck size={14} style={{ color: colors.mainColor }} />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {error && (
        <p className="mt-1.5 text-[11px] font-medium text-red-500 flex items-center gap-1">
          <FiAlertCircle size={12} />
          {isArabic ? "هذا الحقل مطلوب" : "This field is required"}
        </p>
      )}
    </div>
  );
}

function SectionCard({ id, icon, title, children, active }) {
  const { colors, isDark } = useUI();
  return (
    <motion.section
      id={`product-section-${id}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-xl border p-2.5 sm:p-3 space-y-2.5 scroll-mt-3"
      style={{
        borderColor: active ? `${colors.mainColor}55` : colors.border,
        backgroundColor: isDark ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.8)",
      }}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{
            backgroundColor: `${colors.mainColor}18`,
            color: colors.mainColor,
          }}
        >
          {icon}
        </div>
        <h3 className="text-[13px] font-bold" style={{ color: colors.text }}>
          {title}
        </h3>
      </div>
      {children}
    </motion.section>
  );
}

export default function ProductFormFields({
  form,
  setForm,
  fieldErrors,
  setFieldErrors,
  tags,
  categories,
  expandedVariant,
  setExpandedVariant,
  createEmptyColorGroup,
  buildImageUrl,
  calculatePriceAfterDiscount,
  updateColorHex,
  updateColorName,
  handleGoToBrands,
  handleGoToCategories,
  isArabic,
  isDark,
  colors,
}) {
  const [activeSection, setActiveSection] = useState("basics");
  const [dragOver, setDragOver] = useState(false);
  const formRef = useRef(null);

  const applyMainImageFile = (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setForm((f) => ({
      ...f,
      mainImage: { file, existingUrl: url, isChanged: true },
    }));
    if (fieldErrors.mainImage) {
      setFieldErrors((prev) => ({ ...prev, mainImage: false }));
    }
  };

  const sections = [
    {
      id: "basics",
      label: isArabic ? "الأساسيات" : "Basics",
      icon: <FiPackage size={14} />,
    },
    {
      id: "pricing",
      label: isArabic ? "التسعير" : "Pricing",
      icon: <FiDollarSign size={14} />,
    },
    {
      id: "variants",
      label: isArabic ? "المتغيرات" : "Variants",
      icon: <FiLayers size={14} />,
    },
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`product-section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const root = formRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          const id = visible.target.id.replace("product-section-", "");
          if (SECTION_IDS.includes(id)) setActiveSection(id);
        }
      },
      { root: root.closest(".product-modal-scroll") || null, threshold: 0.35 }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(`product-section-${id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const errMsg = (key, ar, en) =>
    fieldErrors[key] ? (isArabic ? ar : en) : false;

  return (
    <div ref={formRef} className="space-y-2.5">
      {/* Section nav */}
      <div
        className="sticky top-0 z-20 -mx-1 px-1 py-0.5 flex gap-1 overflow-x-auto scrollbar-none"
        style={{
          background: `linear-gradient(to bottom, ${colors.cardBg} 70%, transparent)`,
        }}
      >
        {sections.map((s) => {
          const active = activeSection === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all duration-150 shrink-0"
              style={{
                backgroundColor: active ? colors.mainColor : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: active ? (isDark ? "#1e293b" : "#fff") : colors.subText,
              }}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>

      {/* ===== BASICS ===== */}
      <SectionCard
        id="basics"
        active={activeSection === "basics"}
        icon={<FiImage size={14} />}
        title={isArabic ? "معلومات المنتج" : "Product Info"}
      >
        {/* Main image — compact row */}
        <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2.5 items-start">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-[11px] font-semibold flex items-center gap-0.5"
                style={{ color: fieldErrors.mainImage ? "#EF4444" : colors.subText }}
              >
                {isArabic ? "الصورة الرئيسية" : "Main Image"}
                <span className="text-red-500">*</span>
              </span>
            </div>
            <label
              className="group relative block w-full aspect-square max-h-[140px] rounded-xl border border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-all duration-150"
              style={{
                backgroundColor: dragOver
                  ? `${colors.mainColor}12`
                  : isDark
                    ? "rgba(255,255,255,0.02)"
                    : "#f8fafc",
                borderColor: fieldErrors.mainImage
                  ? "#EF4444"
                  : dragOver
                    ? colors.mainColor
                    : colors.border,
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                applyMainImageFile(e.dataTransfer.files?.[0]);
              }}
            >
              <input
                type="file"
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => applyMainImageFile(e.target.files?.[0])}
              />

              {form.mainImage?.existingUrl ? (
                <div className="relative w-full h-full flex items-center justify-center bg-white dark:bg-slate-900">
                  <img
                    src={buildImageUrl(form.mainImage.existingUrl)}
                    className="max-w-full max-h-full object-contain p-1.5"
                    alt="Preview"
                  />
                  <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity text-white">
                    <FiUploadCloud size={18} />
                    <span className="text-[10px] mt-1 font-medium">
                      {isArabic ? "تغيير" : "Change"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center px-2 py-3" style={{ color: colors.subText }}>
                  <FiUploadCloud size={22} className="mx-auto mb-1 opacity-50" style={{ color: colors.mainColor }} />
                  <p className="text-[10px] font-semibold leading-tight" style={{ color: colors.text }}>
                    {isArabic ? "اسحب أو اضغط" : "Drop or click"}
                  </p>
                </div>
              )}
            </label>
            {fieldErrors.mainImage && (
              <p className="mt-1 text-[10px] font-medium text-red-500 flex items-center gap-1">
                <FiAlertCircle size={11} />
                {isArabic ? "مطلوبة" : "Required"}
              </p>
            )}
          </div>

          <div className="space-y-2.5 min-w-0">
            {/* Product type — compact segmented */}
            <div>
              <span className="text-[11px] font-semibold mb-1 block" style={{ color: colors.subText }}>
                {isArabic ? "نوع المنتج" : "Product Type"}
              </span>
              <div
                className="flex rounded-lg p-0.5 border h-[34px]"
                style={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                  borderColor: colors.border,
                }}
              >
                {[
                  { value: false, title: isArabic ? "عادي" : "Standard" },
                  { value: true, title: isArabic ? "فردي" : "Single" },
                ].map((opt) => {
                  const selected = form.isSingleProduct === opt.value;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          isSingleProduct: opt.value,
                          categoryId: opt.value ? 0 : f.categoryId,
                          tagsId: opt.value ? 0 : f.tagsId,
                        }));
                      }}
                      className={`flex-1 rounded-md text-[11px] font-bold transition-all ${
                        selected ? "shadow-sm" : "opacity-50"
                      }`}
                      style={{
                        backgroundColor: selected ? colors.mainColor : "transparent",
                        color: selected ? (isDark ? "#1e293b" : "#fff") : colors.text,
                      }}
                    >
                      {opt.title}
                    </button>
                  );
                })}
              </div>
            </div>

            <ModernField
              label={isArabic ? "اسم المنتج" : "Product Name"}
              value={form.name}
              required
              error={errMsg("name", "اسم المنتج مطلوب", "Product name is required")}
              placeholder={isArabic ? "مثال: قميص قطني" : "e.g. Cotton Shirt"}
              onChange={(v) => {
                setForm((f) => ({ ...f, name: v }));
                if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: false }));
              }}
            />
          </div>
        </div>

        {!form.isSingleProduct && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <SearchableSelect
              label={isArabic ? "البراند" : "Brand"}
              value={form.tagsId || 0}
              options={tags}
              required
              error={!!fieldErrors.tagsId}
              placeholder={isArabic ? "اختر البراند" : "Select Brand"}
              onChange={(selectedId) => {
                setForm((f) => ({ ...f, tagsId: selectedId }));
                if (fieldErrors.tagsId) setFieldErrors((prev) => ({ ...prev, tagsId: false }));
              }}
              action={
                <button
                  type="button"
                  onClick={handleGoToBrands}
                  className="p-0.5 rounded transition-all active:scale-110"
                  style={{ color: colors.gold }}
                  title={isArabic ? "إضافة براند" : "Add Brand"}
                >
                  <FiPlus size={14} strokeWidth={3} />
                </button>
              }
            />

            <SearchableSelect
              label={isArabic ? "القسم" : "Category"}
              value={form.categoryId || 0}
              options={categories}
              required
              error={!!fieldErrors.categoryId}
              placeholder={isArabic ? "اختر القسم" : "Select Category"}
              onChange={(selectedId) => {
                setForm((f) => ({ ...f, categoryId: selectedId, subCategoryId: 0 }));
                if (fieldErrors.categoryId)
                  setFieldErrors((prev) => ({ ...prev, categoryId: false }));
              }}
              action={
                <button
                  type="button"
                  onClick={handleGoToCategories}
                  className="p-0.5 rounded transition-all active:scale-110"
                  style={{ color: colors.gold }}
                  title={isArabic ? "إضافة قسم" : "Add Category"}
                >
                  <FiPlus size={14} strokeWidth={3} />
                </button>
              }
            />

            <SearchableSelect
              label={isArabic ? "القسم الفرعي" : "Sub Category"}
              value={form.subCategoryId || 0}
              options={
                categories.find((c) => Number(c.id) === Number(form.categoryId))
                  ?.subCategories || []
              }
              disabled={!form.categoryId}
              placeholder={isArabic ? "بدون قسم فرعي" : "No Sub-category"}
              onChange={(selectedId) =>
                setForm((f) => ({ ...f, subCategoryId: selectedId }))
              }
            />
          </div>
        )}

        {/* Description */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-[11px] font-semibold" style={{ color: colors.subText }}>
              {isArabic ? "الوصف المختصر" : "Short Description"}
              <span className="opacity-50 font-normal ms-1">
                ({isArabic ? "اختياري" : "Optional"})
              </span>
            </span>
            <span
              className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${
                form.description?.length > 45
                  ? "bg-red-500/10 text-red-600"
                  : "opacity-50"
              }`}
            >
              {form.description?.length || 0}/50
            </span>
          </div>
          <textarea
            rows={1}
            value={form.description || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 50) setForm((f) => ({ ...f, description: val }));
            }}
            placeholder={
              isArabic ? "وصف مختصر..." : "Brief description..."
            }
            className="w-full rounded-lg px-2.5 py-1.5 outline-none transition-all duration-150 resize-none border text-[13px]"
            style={{
              backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#f8fafc",
              borderColor:
                form.description?.length >= 48 ? "#f59e0b" : colors.border,
              color: colors.text,
            }}
          />
        </div>
      </SectionCard>

      {/* ===== PRICING ===== */}
      <SectionCard
        id="pricing"
        active={activeSection === "pricing"}
        icon={<FiTag size={14} />}
        title={isArabic ? "الخصم العام" : "Global Discount"}
      >
        <div className="grid grid-cols-2 gap-2">
          <ModernField
            label={isArabic ? "قيمة الخصم" : "Discount Value"}
            type="number"
            value={form.discountValue}
            suffix={form.discountType === 1 ? "%" : isArabic ? "د.ع" : "IQD"}
            onChange={(v) => {
              const val = parseFloat(v) || 0;
              setForm((f) => ({ ...f, discountValue: val }));
            }}
          />

          <div>
            <span
              className="text-[11px] font-semibold mb-1 block"
              style={{ color: colors.subText }}
            >
              {isArabic ? "نوع الخصم" : "Discount Type"}
            </span>
            <div
              className="flex rounded-lg p-0.5 border h-[34px]"
              style={{
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                borderColor: colors.border,
              }}
            >
              {[
                { type: 0, label: isArabic ? "ثابت" : "Fixed" },
                { type: 1, label: isArabic ? "%" : "%" },
              ].map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, discountType: opt.type }))}
                  className={`flex-1 rounded-md text-[11px] font-bold transition-all ${
                    form.discountType === opt.type ? "shadow-sm" : "opacity-45"
                  }`}
                  style={{
                    backgroundColor:
                      form.discountType === opt.type ? colors.mainColor : "transparent",
                    color:
                      form.discountType === opt.type
                        ? isDark
                          ? "#1e293b"
                          : "#fff"
                        : colors.text,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className="rounded-lg border px-2.5 py-1.5 space-y-1"
          style={{
            backgroundColor: isDark ? "#0f172a" : "#fff",
            borderColor: colors.border,
          }}
        >
          <p className="text-[9px] font-bold uppercase tracking-wider opacity-45">
            {isArabic ? "معاينة السعر" : "Price preview"}
          </p>
          {form.variants.map((v, idx) => {
            const finalPrice = calculatePriceAfterDiscount(
              v.price,
              form.discountValue,
              form.discountType
            );
            const cost = parseFloat(v.costPrice) || 0;
            const isZeroOrLess = finalPrice <= 0 && v.price > 0;
            const isLoss = finalPrice < cost && cost > 0;

            return (
              <div
                key={idx}
                className="flex justify-between items-center gap-2"
              >
                <span className="text-[11px] opacity-70 truncate">
                  {v.variantName || `${isArabic ? "متغير" : "Variant"} ${idx + 1}`}
                </span>
                <div className="flex flex-col items-end shrink-0">
                  <span
                    className="text-[12px] font-bold"
                    style={{
                      color: isZeroOrLess || isLoss ? "#ef4444" : colors.gold,
                    }}
                  >
                    {finalPrice.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                  </span>
                  {isZeroOrLess && (
                    <span className="text-[9px] text-red-500 font-bold">
                      {isArabic ? "> 0 مطلوب" : "Must be > 0"}
                    </span>
                  )}
                  {isLoss && !isZeroOrLess && (
                    <span className="text-[9px] text-red-500 font-bold">
                      {isArabic ? "خسارة" : "Loss"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

{/* ===== VARIANTS ===== */}
      <SectionCard
        id="variants"
        active={activeSection === "variants"}
        icon={<FiLayers size={14} />}
        title={isArabic ? "المتغيرات" : "Variants"}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold" style={{ color: colors.subText }}>
            {form.variants.length} {isArabic ? "متغير" : "variant(s)"}
          </span>
          <button
            type="button"
            onClick={() => {
              setForm((f) => ({
                ...f,
                variants: [
                  ...f.variants,
                  {
                    variantName: "",
                    price: "",
                    costPrice: "",
                    lowStockValue: 0,
                    colorGroups: [createEmptyColorGroup()],
                  },
                ],
              }));
              setExpandedVariant(form.variants.length);
            }}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-transform active:scale-95"
            style={{ backgroundColor: colors.mainColor, color: isDark ? "#1e293b" : "#fff" }}
          >
            <FiPlus size={12} />
            {isArabic ? "متغير جديد" : "New Variant"}
          </button>
        </div>

        <div className="space-y-2">
          {form.variants.map((v, vidx) => {
            const isExpanded = expandedVariant === vidx;
            return (
              <div
                key={vidx}
                className="border rounded-xl overflow-hidden transition-all"
                style={{
                  borderColor: isExpanded ? `${colors.mainColor}55` : colors.border,
                  backgroundColor: isDark ? "#0f172a" : "#ffffff",
                }}
              >
                <div
                  className="px-2.5 py-2 flex items-center justify-between cursor-pointer select-none gap-2"
                  style={{
                    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  }}
                  onClick={() => setExpandedVariant(vidx === expandedVariant ? -1 : vidx)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-black shrink-0"
                      style={{
                        backgroundColor: colors.mainColor,
                        color: isDark ? "#1e293b" : "#fff",
                      }}
                    >
                      {vidx + 1}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-[12px] truncate">
                        {v.variantName || (isArabic ? "متغير جديد" : "New Variant")}
                      </span>
                      {v.price !== "" && v.price != null && (
                        <span className="text-[10px] font-bold opacity-60" style={{ color: colors.gold }}>
                          {v.price} {isArabic ? "د.ع" : "IQD"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {form.variants.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const updated = [...form.variants];
                          updated.splice(vidx, 1);
                          setForm((f) => ({ ...f, variants: updated }));
                        }}
                        className="p-1.5 rounded-lg text-red-500"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ color: colors.mainColor }}
                    >
                      <FiChevronDown size={16} />
                    </motion.div>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                    >
                      <div className="p-2.5 space-y-2.5 border-t" style={{ borderColor: colors.border }}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <ModernField
                            label={isArabic ? "اسم المتغير" : "Variant Name"}
                            value={v.variantName}
                            required
                            error={errMsg(`variants[${vidx}].variantName`, "مطلوب", "Required")}
                            onChange={(val) => {
                              const updated = [...form.variants];
                              updated[vidx].variantName = val;
                              setForm((f) => ({ ...f, variants: updated }));
                              if (fieldErrors[`variants[${vidx}].variantName`]) {
                                const newErrs = { ...fieldErrors };
                                delete newErrs[`variants[${vidx}].variantName`];
                                setFieldErrors(newErrs);
                              }
                            }}
                          />
                          <ModernField
                            label={isArabic ? "سعر الكلفة" : "Cost Price"}
                            type="number"
                            value={v.costPrice}
                            required
                            suffix={isArabic ? "د.ع" : "IQD"}
                            error={errMsg(`variants[${vidx}].costPrice`, "مطلوب", "Required")}
                            onChange={(val) => {
                              const updated = [...form.variants];
                              updated[vidx].costPrice = val;
                              setForm((f) => ({ ...f, variants: updated }));
                              if (fieldErrors[`variants[${vidx}].costPrice`]) {
                                const newErrs = { ...fieldErrors };
                                delete newErrs[`variants[${vidx}].costPrice`];
                                setFieldErrors(newErrs);
                              }
                            }}
                          />
                          <ModernField
                            label={isArabic ? "سعر البيع" : "Selling Price"}
                            type="number"
                            value={v.price}
                            required
                            suffix={isArabic ? "د.ع" : "IQD"}
                            error={errMsg(`variants[${vidx}].price`, "مطلوب", "Required")}
                            onChange={(val) => {
                              const updated = [...form.variants];
                              updated[vidx].price = val;
                              setForm((f) => ({ ...f, variants: updated }));
                              if (fieldErrors[`variants[${vidx}].price`]) {
                                const newErrs = { ...fieldErrors };
                                delete newErrs[`variants[${vidx}].price`];
                                setFieldErrors(newErrs);
                              }
                            }}
                          />
                        </div>

                        <div className="space-y-2 pt-2 border-t border-dashed" style={{ borderColor: colors.border }}>
                          <div className="flex flex-wrap justify-between items-center gap-1.5">
                            <span className="text-[11px] font-bold flex items-center gap-1.5">
                              <span className="w-1 h-3 rounded-full" style={{ backgroundColor: colors.gold }} />
                              {isArabic ? "الألوان" : "Colors"}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...form.variants];
                                updated[vidx].colorGroups.push(createEmptyColorGroup());
                                setForm((f) => ({ ...f, variants: updated }));
                              }}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg font-bold text-[10px] active:scale-105 transition-transform"
                              style={{ backgroundColor: colors.gold, color: "#1e293b" }}
                            >
                              <FiPlus size={11} />
                              {isArabic ? "لون" : "Color"}
                            </button>
                          </div>

                          <div className="space-y-2">
                            {v.colorGroups.map((cg, cgIdx) => {
                              const applyColorImage = (file) => {
                                if (!file || !file.type?.startsWith("image/")) return;
                                const url = URL.createObjectURL(file);
                                setForm((f) => {
                                  const newVariants = [...f.variants];
                                  const newV = { ...newVariants[vidx] };
                                  newV.colorGroups = [...newV.colorGroups];
                                  newV.colorGroups[cgIdx] = {
                                    ...newV.colorGroups[cgIdx],
                                    image: { file, existingUrl: url, isChanged: true },
                                  };
                                  newVariants[vidx] = newV;
                                  return { ...f, variants: newVariants };
                                });
                              };

                              return (
                                <div
                                  key={cgIdx}
                                  className="p-2 rounded-xl border space-y-2"
                                  style={{
                                    borderColor: isDark ? "#1e293b" : "#e2e8f0",
                                    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                                  }}
                                >
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <input
                                      type="color"
                                      value={cg.color?.name || "#000000"}
                                      onChange={(e) => updateColorHex(vidx, cgIdx, e.target.value)}
                                      className="w-8 h-8 rounded-full border-none cursor-pointer shrink-0"
                                    />
                                    <input
                                      type="text"
                                      value={cg.color?.colorName || ""}
                                      onChange={(e) => updateColorName(vidx, cgIdx, e.target.value)}
                                      placeholder={isArabic ? "اسم اللون" : "Color name"}
                                      className="flex-1 min-w-[90px] rounded-lg px-2 py-1.5 text-[12px] border outline-none"
                                      style={{
                                        borderColor: isDark ? "#334155" : "#e2e8f0",
                                        color: colors.text,
                                        backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#fff",
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setForm((f) => {
                                          const newVariants = [...f.variants];
                                          const newV = { ...newVariants[vidx] };
                                          newV.colorGroups = [...newV.colorGroups];
                                          newV.colorGroups[cgIdx] = {
                                            ...newV.colorGroups[cgIdx],
                                            color: {
                                              ...newV.colorGroups[cgIdx].color,
                                              name: "",
                                              colorName: "",
                                              isSelected: false,
                                            },
                                          };
                                          newVariants[vidx] = newV;
                                          return { ...f, variants: newVariants };
                                        });
                                      }}
                                      className="px-2 py-1.5 rounded-lg text-[10px] font-bold border shrink-0"
                                      style={{ borderColor: isDark ? "#334155" : "#e2e8f0", color: colors.text }}
                                    >
                                      {isArabic ? "بدون" : "None"}
                                    </button>
                                    {v.colorGroups.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setForm((f) => {
                                            const newVariants = [...f.variants];
                                            const newV = { ...newVariants[vidx] };
                                            newV.colorGroups = [...newV.colorGroups];
                                            newV.colorGroups.splice(cgIdx, 1);
                                            newVariants[vidx] = newV;
                                            return { ...f, variants: newVariants };
                                          });
                                        }}
                                        className="p-1.5 rounded-lg text-red-500 shrink-0"
                                      >
                                        <FiTrash2 size={13} />
                                      </button>
                                    )}
                                  </div>

                                  {/* Secondary color image — compact horizontal strip */}
                                  <label
                                    className="flex items-center gap-2.5 w-full rounded-lg border border-dashed px-2 py-1.5 cursor-pointer transition-all group/cimg"
                                    style={{
                                      borderColor: isDark ? "#334155" : "#e2e8f0",
                                      backgroundColor: isDark ? "rgba(15,23,42,0.45)" : "#fff",
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      applyColorImage(e.dataTransfer.files?.[0]);
                                    }}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(e) => applyColorImage(e.target.files?.[0])}
                                    />
                                    <div
                                      className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center shrink-0 border"
                                      style={{
                                        borderColor: colors.border,
                                        backgroundColor: isDark ? "#131c2e" : "#f8fafc",
                                      }}
                                    >
                                      {cg.image?.existingUrl ? (
                                        <img
                                          src={cg.image?.file ? cg.image.existingUrl : buildImageUrl(cg.image.existingUrl)}
                                          className="w-full h-full object-cover"
                                          alt=""
                                        />
                                      ) : (
                                        <FiImage size={16} className="opacity-40 group-hover/cimg:opacity-80" style={{ color: colors.mainColor }} />
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[11px] font-semibold truncate" style={{ color: colors.text }}>
                                        {cg.image?.existingUrl
                                          ? (isArabic ? "صورة اللون" : "Color image")
                                          : (isArabic ? "رفع صورة اللون" : "Upload color image")}
                                      </p>
                                      <p className="text-[9px] opacity-50 truncate">
                                        {cg.image?.existingUrl
                                          ? (isArabic ? "اضغط أو اسحب للتغيير" : "Click or drop to change")
                                          : (isArabic ? "اختياري · PNG / JPG" : "Optional · PNG / JPG")}
                                      </p>
                                    </div>
                                    <FiUploadCloud size={14} className="shrink-0 opacity-40 group-hover/cimg:opacity-80" style={{ color: colors.mainColor }} />
                                  </label>

                                  <div className="space-y-1.5">
                                    {cg.sizes.map((sz, szIdx) => (
                                      <div
                                        key={szIdx}
                                        className="flex flex-wrap sm:flex-nowrap items-end gap-1.5 p-1.5 rounded-lg"
                                        style={{
                                          backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                                        }}
                                      >
                                        <div className="flex-1 min-w-[60px]">
                                          {szIdx === 0 && (
                                            <span className="text-[9px] font-bold opacity-40 uppercase block mb-0.5">
                                              {isArabic ? "الحجم" : "Size"}
                                            </span>
                                          )}
                                          <input
                                            type="text"
                                            value={sz.size}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setForm((f) => {
                                                const newVariants = [...f.variants];
                                                const newV = { ...newVariants[vidx] };
                                                newV.colorGroups = [...newV.colorGroups];
                                                const newCG = { ...newV.colorGroups[cgIdx] };
                                                newCG.sizes = [...newCG.sizes];
                                                newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], size: val };
                                                newV.colorGroups[cgIdx] = newCG;
                                                newVariants[vidx] = newV;
                                                return { ...f, variants: newVariants };
                                              });
                                              const errKey = `variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`;
                                              if (fieldErrors[errKey]) {
                                                const newErrs = { ...fieldErrors };
                                                delete newErrs[errKey];
                                                setFieldErrors(newErrs);
                                              }
                                            }}
                                            placeholder={isArabic ? "الحجم" : "Size"}
                                            className="w-full rounded-lg px-2 py-1.5 outline-none border font-semibold text-[11px]"
                                            style={{
                                              backgroundColor: isDark ? "#131c2e" : "#fff",
                                              borderColor: fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`]
                                                ? "#EF4444"
                                                : isDark ? "#1e293b" : "#e2e8f0",
                                              color: colors.text,
                                            }}
                                          />
                                        </div>
                                        <div className="flex-1 min-w-[48px]">
                                          {szIdx === 0 && (
                                            <span className="text-[9px] font-bold opacity-40 uppercase block mb-0.5">
                                              {isArabic ? "الكمية" : "Qty"}
                                            </span>
                                          )}
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            value={sz.quantity}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/[^0-9]/g, "");
                                              setForm((f) => {
                                                const newVariants = [...f.variants];
                                                const newV = { ...newVariants[vidx] };
                                                newV.colorGroups = [...newV.colorGroups];
                                                const newCG = { ...newV.colorGroups[cgIdx] };
                                                newCG.sizes = [...newCG.sizes];
                                                newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], quantity: val };
                                                newV.colorGroups[cgIdx] = newCG;
                                                newVariants[vidx] = newV;
                                                return { ...f, variants: newVariants };
                                              });
                                              const errKey = `variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`;
                                              if (fieldErrors[errKey]) {
                                                const newErrs = { ...fieldErrors };
                                                delete newErrs[errKey];
                                                setFieldErrors(newErrs);
                                              }
                                            }}
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="0"
                                            className="w-full rounded-lg px-2 py-1.5 outline-none border font-semibold text-[11px]"
                                            style={{
                                              backgroundColor: isDark ? "#131c2e" : "#fff",
                                              borderColor: fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`]
                                                ? "#EF4444"
                                                : isDark ? "#1e293b" : "#e2e8f0",
                                              color: colors.text,
                                            }}
                                          />
                                        </div>
                                        <div className="flex-1 min-w-[60px]">
                                          {szIdx === 0 && (
                                            <span className="text-[9px] font-bold uppercase block mb-0.5 text-red-500">
                                              {isArabic ? "نقص مخزون" : "Low Stock"}
                                            </span>
                                          )}
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            value={sz.lowStockValue === 0 || sz.lowStockValue === "0" ? "" : (sz.lowStockValue ?? "")}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/[^0-9]/g, "");
                                              setForm((f) => {
                                                const newVariants = [...f.variants];
                                                const newV = { ...newVariants[vidx] };
                                                newV.colorGroups = [...newV.colorGroups];
                                                const newCG = { ...newV.colorGroups[cgIdx] };
                                                newCG.sizes = [...newCG.sizes];
                                                newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], lowStockValue: val };
                                                newV.colorGroups[cgIdx] = newCG;
                                                newVariants[vidx] = newV;
                                                return { ...f, variants: newVariants };
                                              });
                                            }}
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="0"
                                            className="w-full rounded-lg px-2 py-1.5 outline-none border font-semibold text-[11px]"
                                            style={{
                                              backgroundColor: isDark ? "#131c2e" : "#fff",
                                              borderColor: isDark ? "#1e293b" : "#e2e8f0",
                                              color: colors.text,
                                            }}
                                          />
                                        </div>
                                        <div className="flex gap-0.5 pb-0.5">
                                          {cg.sizes.length > 1 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setForm((f) => {
                                                  const newVariants = [...f.variants];
                                                  const newV = { ...newVariants[vidx] };
                                                  newV.colorGroups = [...newV.colorGroups];
                                                  const newCG = { ...newV.colorGroups[cgIdx] };
                                                  newCG.sizes = [...newCG.sizes];
                                                  newCG.sizes.splice(szIdx, 1);
                                                  newV.colorGroups[cgIdx] = newCG;
                                                  newVariants[vidx] = newV;
                                                  return { ...f, variants: newVariants };
                                                });
                                              }}
                                              className="p-1.5 rounded-lg text-red-400"
                                            >
                                              <FiX size={14} />
                                            </button>
                                          )}
                                          {szIdx === cg.sizes.length - 1 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setForm((f) => {
                                                  const newVariants = [...f.variants];
                                                  const newV = { ...newVariants[vidx] };
                                                  newV.colorGroups = [...newV.colorGroups];
                                                  const newCG = { ...newV.colorGroups[cgIdx] };
                                                  newCG.sizes = [...newCG.sizes, { size: "", quantity: "", lowStockValue: "" }];
                                                  newV.colorGroups[cgIdx] = newCG;
                                                  newVariants[vidx] = newV;
                                                  return { ...f, variants: newVariants };
                                                });
                                              }}
                                              className="p-1.5 rounded-lg"
                                              style={{ color: colors.gold }}
                                              title={isArabic ? "إضافة حجم" : "Add Size"}
                                            >
                                              <FiPlus size={15} strokeWidth={3} />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
